<script setup lang="ts">
import { easeOut, motion } from 'motion-v'

const { t, tm, rt } = useI18n()
const isScreenLG = useMediaQuery('(min-width: 1024px)')

const headlineVariants = {
  initial: {
    opacity: 0,
    y: '50%',
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: easeOut,
    },
  },
}

const contentVariants = {
  initial: (props: { isLarge: boolean, isFlipped: boolean }) => {
    return props.isLarge
      ? {
          opacity: 0,
          y: 0,
          x: props.isFlipped ? -50 : 50,
        }
      : {
          opacity: 0,
          x: 0,
          y: -50,
        }
  },
  animate: (props: { isLarge: boolean }) => {
    return props.isLarge
      ? {
          opacity: 1,
          x: 0,
          y: 0,
          transition: { delay: 0.2, duration: 0.5 },
        }
      : {
          opacity: 1,
          x: 0,
          y: 0,
          transition: { delay: 0.2, duration: 0.5 },
        }
  },
}
</script>

<template>
  <div class="from-[#f8f8f0] from-50% to-white md:rounded-b-[5rem] md:bg-gradient-to-b md:shadow-2xl md:shadow-black/10">
    <div class="w-full rounded-b-[5rem] bg-white pt-2 md:py-12">
      <section class="mx-auto grid max-w-screen-xl grid-cols-1 gap-y-10 px-4 pt-0 md:px-8 md:py-10">
        <div class="rounded-[2.5rem] border border-neutral-200 p-4 md:p-8 lg:p-12">
          <motion.h2
            class="mx-auto max-w-screen-sm text-balance text-center text-2xl lg:text-4xl"
            :variants="headlineVariants"
            initial="initial"
            while-in-view="animate"
            exit="initial"
          >
            {{ t('about.section.neighborhood.title') }}
          </motion.h2>

          <hr class="my-4 border-t border-neutral-200 lg:my-10">

          <div class="grid place-items-center gap-8 lg:grid-cols-2">
            <motion.img
              class="mx-auto aspect-video w-full max-w-md shrink rotate-3 rounded-[2rem] object-cover drop-shadow-lg"
              :src="`${S3_ASSETS_URL}/about/hero-01.jpg`"
              role="presentation"
              :custom="{ isFlipped: true, isLarge: isScreenLG }"
              :variants="contentVariants"
              initial="initial"
              while-in-view="animate"
              exit="initial"
            />

            <motion.div
              class="w-full text-gray-600"
              :custom="{ isFlipped: false, isLarge: isScreenLG }"
              :variants="contentVariants"
              initial="initial"
              while-in-view="animate"
              exit="initial"
            >
              <div
                v-for="(_, index) in tm('about.section.neighborhood.items')"
                :key="index"
                class="pb-4"
              >
                <i18n-t
                  :keypath="`about.section.neighborhood.items.${index}.description`"
                  tag="p"
                >
                  <template #placeholder>
                    <strong class="bold">
                      {{ t(`about.section.neighborhood.items.${index}.placeholder`) }}
                    </strong>
                  </template>
                </i18n-t>
              </div>
            </motion.div>
          </div>
        </div>

        <div class="rounded-[2.5rem] border border-neutral-200 bg-[#F9F9F2] p-4 md:p-8 lg:p-12">
          <motion.h2
            class="mx-auto max-w-screen-sm text-balance text-center text-2xl font-medium text-slate-800 lg:text-4xl"
            :variants="headlineVariants"
            initial="initial"
            while-in-view="animate"
            exit="initial"
          >
            {{ t('about.section.technology.title') }}

            <small class="mt-2 block text-lg font-normal text-gray-600 lg:text-xl">
              {{ t('about.section.technology.subtitle') }}
            </small>
          </motion.h2>

          <hr class="my-4 border-t border-neutral-200 lg:my-10">

          <div
            class="grid grid-cols-1 place-items-start gap-6 md:gap-8 lg:grid-cols-3 lg:gap-12 xl:gap-20"
          >
            <motion.div
              class="text-gray-600 lg:col-span-2"
              :custom="{ isFlipped: true, isLarge: isScreenLG }"
              :variants="contentVariants"
              initial="initial"
              while-in-view="animate"
              exit="initial"
            >
              <div
                v-for="(_, index) in tm('about.section.technology.items')"
                :key="index"
                class="pb-4"
              >
                <i18n-t
                  :keypath="`about.section.technology.items.${index}.description`"
                  tag="p"
                >
                  <template #placeholder>
                    <strong class="bold">
                      {{ t(`about.section.technology.items.${index}.placeholder`) }}
                    </strong>
                  </template>
                </i18n-t>
              </div>
            </motion.div>

            <motion.div
              class="grid grid-cols-1 place-content-start gap-y-6 rounded-2xl border border-neutral-200 bg-white p-8 shadow-md shadow-black/5 lg:col-span-1"
              :custom="{ isFlipped: false, isLarge: isScreenLG }"
              :variants="contentVariants"
              initial="initial"
              while-in-view="animate"
              exit="initial"
            >
              <span class="flex gap-x-0.5">
                <UiIcon
                  v-for="i in 5"
                  :key="i"
                  name="star-solid"
                  class="text-lg text-amber-500"
                />
              </span>

              <p class="font-semibold">
                {{ t('about.section.technology.right_item_placeholder') }}
              </p>

              <div class="flex items-center gap-x-4">
                <div class="size-12 overflow-clip rounded-[50%]">
                  <img
                    :src="`${S3_ASSETS_URL}/about/review-avatar.jpg`"
                    alt="Avatar"
                    class="size-full object-cover"
                  >
                </div>

                <div class="flex flex-col items-start justify-center">
                  <span class="font-semibold">
                    {{ t('about.section.technology.right_item_title') }}
                  </span>

                  <span class="text-sm text-gray-500">
                    {{ t('about.section.technology.right_item_subtitle') }}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            class="mx-auto mt-4 flex max-w-sm items-center lg:mx-0 lg:w-max"
            :initial="{ opacity: 0, y: 50, scale: 0.5 }"
            :while-in-view="{ opacity: 1, y: 0, scale: 1 }"
            :exit="{ opacity: 0, y: 50, scale: 0.5 }"
          >
            <UiButton
              class="group w-full"
              as-child
            >
              <RouterLink :to="{ name: '/free-demo' }">
                {{ t('about.section.technology.action') }}

                <UiIcon
                  name="arrow-forward-rounded"
                  class="hidden transition-transform duration-200 group-hover:translate-x-1 md:visible md:block"
                />
              </RouterLink>
            </UiButton>
          </motion.div>
        </div>

        <div class="rounded-[2.5rem] border border-neutral-200 p-4 md:p-8 lg:p-12">
          <motion.h2
            class="mx-auto max-w-screen-sm text-balance text-center text-2xl lg:text-4xl"
            :variants="headlineVariants"
            initial="initial"
            while-in-view="animate"
            exit="initial"
          >
            {{ t('about.section.ai.title') }}
          </motion.h2>

          <hr class="my-4 border-t border-neutral-200 lg:my-10">

          <div class="grid place-items-center gap-8 lg:grid-cols-2">
            <motion.img
              class="mx-auto aspect-[9/12] w-full max-w-xs shrink -rotate-3 rounded-[2rem] object-cover drop-shadow-lg"
              :src="`${S3_ASSETS_URL}/about/hero-02.jpg`"
              role="presentation"
              :custom="{ isFlipped: true, isLarge: isScreenLG }"
              :variants="contentVariants"
              initial="initial"
              while-in-view="animate"
              exit="initial"
            />

            <motion.div
              class="w-full text-gray-600"
              :custom="{ isFlipped: false, isLarge: isScreenLG }"
              :variants="contentVariants"
              initial="initial"
              while-in-view="animate"
              exit="initial"
            >
              <div
                v-for="(item, itemIndex) in tm('about.section.ai.items')"
                :key="itemIndex"
                class="pb-4"
              >
                <i18n-t
                  :keypath="`about.section.ai.items.${itemIndex}.description`"
                  tag="p"
                >
                  <template
                    v-for="(placeholder, placeholderIndex) in item.placeholder"
                    :key="placeholderIndex"
                    #[`placeholder_${placeholderIndex}`]
                  >
                    <strong class="bold">
                      {{ rt(placeholder!) }}
                    </strong>
                  </template>
                </i18n-t>
              </div>
            </motion.div>
          </div>

          <motion.div
            class="mt-6 grid grid-flow-row auto-rows-auto gap-5 lg:auto-cols-auto lg:grid-flow-col"
            :initial="{ opacity: 0, y: 50, scale: 0.5 }"
            :while-in-view="{ opacity: 1, y: 0, scale: 1 }"
            :exit="{ opacity: 0, y: 50, scale: 0.5 }"
          >
            <div class="w-full rounded-[2rem] border border-[#F1EAE2] bg-[#F9F9F2] p-4 md:p-8">
              <h2 class="pb-4 text-left text-2xl text-slate-800 md:text-4xl">
                {{ t('about.section.ai_second.title') }}
              </h2>

              <p>
                {{ t('about.section.ai_second.subtitle') }}
              </p>

              <UiButton
                class="group mt-4 w-full md:w-fit"
                color="accent"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  {{ t('about.section.ai_second.action') }}

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="hidden transition-transform duration-200 group-hover:translate-x-1 md:visible md:block"
                  />
                </RouterLink>
              </UiButton>
            </div>

            <div class="mx-auto w-full max-w-md overflow-clip rounded-[2rem] lg:max-w-xs">
              <img
                :src="`${S3_ASSETS_URL}/about/hero-03.jpg`"
                role="presentation"
                class="aspect-video size-full object-cover"
              >
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  </div>
</template>
